import { openDB } from 'idb';
import { DATA_VERSION, INBOX_CACHE_INVALIDATION_VERSION } from './version';

const DB_OPEN_TIMEOUT_MS = 1500;

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team', 'canned_response'];
    this.accountId = accountId;
    this.db = null;
    this.dbPromise = null;
  }

  async initDb() {
    if (this.db) return this.db;
    const dbName = `cw-store-${this.accountId}`;
    this.dbPromise ||= new Promise((resolve, reject) => {
      let abandoned = false;
      // Opens queued behind another tab's blocked request receive no blocked
      // event of their own. Cache availability must not hold up network reads.
      const timeout = setTimeout(() => {
        abandoned = true;
        reject(new Error('IndexedDB opening timed out'));
      }, DB_OPEN_TIMEOUT_MS);
      openDB(dbName, DATA_VERSION, {
        blocked() {
          // An older tab can hold the previous schema open indefinitely. Let the
          // API client use the network, retaining this rejection until reload.
          abandoned = true;
          clearTimeout(timeout);
          reject(new Error('IndexedDB upgrade blocked by another tab'));
        },
        blocking: () => {
          this.db?.close();
          this.db = null;
          this.dbPromise = null;
        },
        upgrade(db, oldVersion, _newVersion, transaction) {
          const shouldInvalidateInboxCache =
            oldVersion > 0 && oldVersion < INBOX_CACHE_INVALIDATION_VERSION;

          if (shouldInvalidateInboxCache) {
            transaction.objectStore('inbox').clear();
            transaction.objectStore('cache-keys').delete('inbox');
          }

          // Existing databases already carry the stores added in earlier versions,
          // and createObjectStore throws on a name that is already taken.
          const createStore = (name, options) => {
            if (db.objectStoreNames.contains(name)) return;
            db.createObjectStore(name, options);
          };

          createStore('cache-keys');
          createStore('inbox', { keyPath: 'id' });
          createStore('label', { keyPath: 'id' });
          createStore('team', { keyPath: 'id' });
          createStore('canned_response', { keyPath: 'id' });
        },
      }).then(
        db => {
          clearTimeout(timeout);
          // A blocked open cannot be cancelled. Release its late connection so it
          // cannot hold up another tab's upgrade after we have fallen back.
          if (abandoned) {
            db.close();
            return;
          }
          resolve(db);
        },
        error => {
          clearTimeout(timeout);
          reject(error);
        }
      );
    });
    this.db = await this.dbPromise;

    // Store the database name in LocalStorage
    const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
    if (!dbNames.includes(dbName)) {
      dbNames.push(dbName);
      localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
    }

    return this.db;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    await this.db.clear(modelName);
    return this.push({ modelName, data });
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  // Patches a single cached record in place, without touching the cache key, so it
  // does not trigger a full refetch on the next cold load.
  async update({ modelName, id, data }) {
    this.validateModel(modelName);
    const record = await this.db.get(modelName, Number(id));
    if (!record) return;
    await this.db.put(modelName, { ...record, ...data });
  }

  async setCacheKeys(cacheKeys) {
    Object.keys(cacheKeys).forEach(async modelName => {
      this.db.put('cache-keys', cacheKeys[modelName], modelName);
    });
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
